import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import type { MonthlyExtractFileShare } from "shared/monthlyExtractRun"
import {
  MonthlyExtractRunDto,
  useCreateMonthlyExtractShareMutation,
} from "src/features/things/monthlyExtractApi"
import { shareExpiry } from "./monthlyReportsChrome"

const CopyButton: FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false)
  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
      <IconButton
        size="small"
        aria-label={`copy ${label}`}
        onClick={() =>
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          })
        }
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const ShareDetails: FC<{ share: MonthlyExtractFileShare; filename: string }> = ({ share, filename }) => {
  const expiry = shareExpiry(share.shareExpiresAt)
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem" }} data-testid="share-url">
          {share.shareUrl}
        </Typography>
        <CopyButton value={share.shareUrl} label={`link of ${filename}`} />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2">PIN</Typography>
        <Typography
          sx={{ fontFamily: "monospace", fontSize: "1rem", fontWeight: 700, letterSpacing: "0.15em" }}
          data-testid="share-pin"
        >
          {share.sharePin ?? "—"}
        </Typography>
        {share.sharePin && <CopyButton value={share.sharePin} label={`PIN of ${filename}`} />}
      </Stack>
      <Typography
        variant="body2"
        sx={{ color: expiry.expired ? "error.main" : "text.secondary" }}
        data-testid="share-expiry"
      >
        Expires {expiry.label}
      </Typography>
    </Stack>
  )
}

/** One report: its current share, if any, and the button that creates (or replaces) it. */
const ReportShare: FC<{ run: MonthlyExtractRunDto; fileKey: string }> = ({ run, fileKey }) => {
  const [createShare, { isLoading, error }] = useCreateMonthlyExtractShareMutation()
  const share = run.shares?.[fileKey]
  const filename = fileKey.slice(fileKey.lastIndexOf("/") + 1)
  const expired = !!share && shareExpiry(share.shareExpiresAt).expired

  return (
    <Box data-testid={`report-share-${filename}`}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all" }}>
          {filename}
        </Typography>
        {run.status === "done" && (
          <Button
            variant={!share || expired ? "contained" : "outlined"}
            size="small"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            disabled={isLoading}
            onClick={() => void createShare({ runId: run.id, key: fileKey })}
            aria-label={`${share ? "New share link" : "Create share link"} for ${filename}`}
          >
            {isLoading ? "Creating…" : share ? "New share link" : "Create share link"}
          </Button>
        )}
      </Stack>
      {share ? (
        <ShareDetails share={share} filename={filename} />
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Not shared.
        </Typography>
      )}
      {!!error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {(error as any)?.data?.message ?? "could not create a share link"}
        </Alert>
      )}
    </Box>
  )
}

/**
 * LOCKER-500 DoD4 / N6 — one partner share per report. A run shares nothing;
 * each click makes a 30-day PIN share of that one `.xlsx` and replaces only that
 * report's previous share. Link and PIN each have a copy button: an admin sends
 * both to the partner.
 */
export const SharePanel: FC<{ run: MonthlyExtractRunDto }> = ({ run }) => {
  const workbooks = run.s3Keys?.workbooks ?? []
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-ui="UI-SL2W-281">
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Partner share links</Typography>
      {workbooks.length ? (
        <Stack spacing={2}>
          {workbooks.map((key) => (
            <ReportShare key={key} run={run} fileKey={key} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No report to share on this run.
        </Typography>
      )}
    </Paper>
  )
}
